import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { ConstructionTab } from '../../../reducers/construction'
import ItemListSwitcher from '../../TakeOff/ItemListSwitcher'
import { useCallback, useMemo } from 'react'
import BuildingViewModes from '../Components/BuildingViewModes'
import CommentsClusterComponent from '../../Comments/CommentsClusterComponent'
import { BuildingElementStatus } from '../../../models/Construction'
import { createSelector } from '@reduxjs/toolkit'

interface RightToolbarPanelProps {}

const selectComments = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.comments.commentClusters,
    (state: RootState) => state.comments.commentsDisciplineFilter,
  ],
  (selectedConstructionTab, commentClusters, commentsDisciplineFilter) => {
    const tab = commentClusters.find(
      (cluster) => cluster.tab === selectedConstructionTab,
    )

    return tab
      ? commentsDisciplineFilter
        ? tab.comments.filter(
            (comment) => comment.discipline === commentsDisciplineFilter,
          )
        : tab.comments
      : []
  },
)

const selectIsDisabledForEditorTab = createSelector(
  (state: RootState) => state.construction.buildingElementStatus,
  (buildingElementStatus) => {
    return buildingElementStatus.some(
      (element) => element.status !== BuildingElementStatus.Complete,
    )
  },
)

const RightToolbarPanel: React.FC<RightToolbarPanelProps> = () => {
  const comments = useSelector(selectComments)
  const isDisabledForEditorTab = useSelector(selectIsDisabledForEditorTab)
  const selectedConstructionTab = useSelector(
    (state: RootState) => state.construction.selectedConstructionTab,
  )

  const transparentBackground = useMemo(() => {
    const allowedConstructionTabs = [
      ConstructionTab.Editor,
      ConstructionTab.TakeOff,
      ConstructionTab.Validation,
    ]

    return allowedConstructionTabs.includes(selectedConstructionTab)
  }, [selectedConstructionTab])

  const renderRightToolbarPanelContent = useCallback(() => {
    switch (selectedConstructionTab) {
      case ConstructionTab.TakeOff:
        return (
          <Box
            sx={{
              display: 'flex',
              padding: '10px',
              flexDirection: 'column-reverse',
              justifyContent: 'space-between',
              alignItems: 'end',
            }}
          >
            <ItemListSwitcher />
          </Box>
        )

      case ConstructionTab.Validation:
        return (
          <Box
            sx={{
              width: '320px',
              display: 'flex',
              padding: '10px',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'end',
            }}
          >
            <BuildingViewModes reversed />
          </Box>
        )

      case ConstructionTab.Editor:
        return (
          <Box
            gap='1rem'
            sx={{
              width: '10rem',
              display: 'flex',
              flexDirection: 'column',
              mr: '0.5rem',
              mt: '0.5rem',
              pointerEvents: 'auto',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Box
                sx={{
                  width: '32px',
                }}
              >
                <CommentsClusterComponent
                  tab={ConstructionTab.Editor}
                  comments={comments}
                  isDisabled={isDisabledForEditorTab}
                />
              </Box>
            </Box>
          </Box>
        )
    }

    return null
  }, [selectedConstructionTab, comments, isDisabledForEditorTab])

  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: '4px',
        boxSizing: 'border-box',
        margin: `${transparentBackground ? 0 : 12}px`,
        pointerEvents: transparentBackground ? 'none' : 'auto',
        background: transparentBackground ? 'transparent' : 'white',
        boxShadow: transparentBackground
          ? 'none'
          : '0px 3px 5px 0px rgba(47, 47, 47, 0.25)',
      }}
    >
      {renderRightToolbarPanelContent()}
    </Box>
  )
}

export default RightToolbarPanel
